/**
 * Shared types, sorters and formatters for the Affiliates feature.
 *
 * Lives outside `Affiliates.tsx` so the affiliate detail pages
 * (e.g. `AffiliateProfile/AffiliatePromoCodes`) can render rows with the
 * same shape and formatting helpers.
 */

import type {
  AffiliateKpiValue,
  OperatorAffiliateKpiKey,
  PlanType,
} from "../../types/affiliates";
import { formatCurrency as formatCurrencyBase } from "../../utils/financialFormatters";

export type AffiliateStatus = "active" | "deactivated";

// "affiliate" scope (vs "module") drops the affiliate filters + Top 5
// Affiliates and widens Top 5 Codes — the affiliate is already fixed.
export type AffiliateKpisScope = "module" | "affiliate";

// The 8 metric fields shared by the affiliate list, profile-codes and
// portal-codes table rows. `cpaAmount` is null on non-CPA plans (BE
// sentinel) so the UI can render "N/A" instead of $0.00.
export type MetricRowFields = Pick<
  AffiliateRow,
  | "totalUsers"
  | "totalFtds"
  | "ftdRate"
  | "cpaAmount"
  | "sportsbookPnl"
  | "casinoNgr"
  | "totalOwed"
  | "totalRevenue"
>;

// BE KPI key (UPPER_SNAKE) → FE row field (camel). Single source of truth
// so the three table mappers never drift.
const METRIC_KEY_TO_FIELD: Record<
  OperatorAffiliateKpiKey,
  keyof MetricRowFields
> = {
  TOTAL_USERS: "totalUsers",
  TOTAL_FTDS: "totalFtds",
  FTD_RATE: "ftdRate",
  CPA_AMOUNT: "cpaAmount",
  SPORTSBOOK_PL: "sportsbookPnl",
  CASINO_NGR: "casinoNgr",
  TOTAL_OWED: "totalOwed",
  TOTAL_REVENUE: "totalRevenue",
};

const EMPTY_METRIC_FIELDS: MetricRowFields = {
  totalUsers: 0,
  totalFtds: 0,
  ftdRate: 0,
  cpaAmount: null,
  sportsbookPnl: 0,
  casinoNgr: 0,
  totalOwed: 0,
  totalRevenue: 0,
};

// Per-promo-code `metrics` block: CPA_AMOUNT keeps null (rendered N/A); the
// other 7 KPIs coerce missing → 0. Missing block → cpaAmount stays null.
export const mapCodeMetricsToRow = (
  metrics: Partial<Record<OperatorAffiliateKpiKey, number | null>> | undefined,
): MetricRowFields => {
  const row = { ...EMPTY_METRIC_FIELDS };
  if (!metrics) return row;
  (Object.keys(METRIC_KEY_TO_FIELD) as OperatorAffiliateKpiKey[]).forEach(
    (key) => {
      const field = METRIC_KEY_TO_FIELD[key];
      if (field === "cpaAmount") {
        row.cpaAmount = metrics[key] ?? null;
      } else {
        row[field] = metrics[key] ?? 0;
      }
    },
  );
  return row;
};

// List-item / detail `metrics` block: `{ value, change_pct }`. Reads `.value`.
// CPA_AMOUNT keeps `null` (rendered as N/A); the other 7 KPIs coerce to 0.
export const mapKpiMetricsToRow = (
  metrics:
    | Partial<Record<OperatorAffiliateKpiKey, AffiliateKpiValue>>
    | undefined,
): MetricRowFields => {
  const row = { ...EMPTY_METRIC_FIELDS };
  if (!metrics) return row;
  (Object.keys(METRIC_KEY_TO_FIELD) as OperatorAffiliateKpiKey[]).forEach(
    (key) => {
      const field = METRIC_KEY_TO_FIELD[key];
      if (field === "cpaAmount") {
        row.cpaAmount = metrics[key]?.value ?? null;
      } else {
        row[field] = metrics[key]?.value ?? 0;
      }
    },
  );
  return row;
};

// Pull a single KPI tile out of a `{ value, change_pct }` block for a KPI
// card. `change_pct` null (zero/untrusted prior period) → no trend indicator.
// Generic over the key enum so portal (5-key subset) reuses the same helper.
export const resolveKpiTile = <K extends string>(
  kpis: Partial<Record<K, AffiliateKpiValue>> | undefined,
  key: K,
): { value: number; percentage: number | undefined } => {
  const tile = kpis?.[key];
  return { value: tile?.value ?? 0, percentage: tile?.change_pct ?? undefined };
};

export type AffiliateRow = {
  key: string;
  id?: string;
  name?: string;
  paymentModel?: PlanType | null;
  // Shared with the promo codes table / PAM view — not list-only.
  referralCode?: string;
  promoCodeCount?: number;
  totalUsers: number;
  totalFtds: number;
  ftdRate: number;
  cpaAmount: number | null;
  sportsbookPnl: number;
  casinoNgr: number;
  totalOwed: number;
  totalRevenue: number;
  status?: AffiliateStatus;
  children?: AffiliateRow[];
};

export const numberSorter =
  (key: keyof AffiliateRow) => (a: AffiliateRow, b: AffiliateRow) => {
    const valueA = Number(a?.[key] ?? 0);
    const valueB = Number(b?.[key] ?? 0);
    return valueA - valueB;
  };

// Affiliate tables always render two decimals (money columns). Thin wrapper
// over the shared util so callers that haven't been wired to
// `useCurrencySymbol()` yet keep working with the "$" default.
export const formatCurrency = (value: number, symbol: string = "$"): string =>
  formatCurrencyBase(value, symbol, 2);

export const formatNumber = (value: number): string =>
  new Intl.NumberFormat("en-US").format(value);

export const formatPercentage = (value: number): string =>
  `${value.toFixed(2)}%`;
